import type { UIInventoryItem } from '@sorrem-client/types';
import { EssenceVessel, Loadout, useItemTransferContext } from '@sorrem-client/ui/components';
import { useAbilityPickerMenu, type AbilityPickerMenu } from '@sorrem-client/ui/features/AbilityPicker';
import { useIsRunHudTourPending } from '@sorrem-client/ui/features/GuidedTour';
import { HudAreaSlot } from '@sorrem-client/ui/features/Hud';
import { useEquipCarriedItem, useLoadoutActionPress } from '@sorrem-client/ui/features/GameOverlay/hooks';
import { useLoadoutActions } from '@sorrem-client/ui/features/loadout-actions';
import { useLoadoutEquipment } from '@sorrem-client/ui/features/loadout-equipment';
import { useGameplay } from '@sorrem-client/ui/features/PlayPage/gameplay-context';
import { useActionBarTargets, useDrawActionCallout, useGameSelector, useHudPanels, useInputBindingPress } from '@sorrem-client/ui/hooks';
import { listCarriedSlotHotkeyIndexes } from '@sorrem-client/ui/shared/action-bar';
import { getStoredLoadout, isBothHanded } from '@sorrem/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActiveAffliction } from '../ActiveAffliction';
import { ActiveCrystalEffects } from '../ActiveCrystalEffects';
import { RunCarriedPanel } from '../RunCarriedPanel';
import { toDrawPulseSweep } from './draw-pulse-sweep';
import styles from './GameOverlayHud.module.scss';

interface GameOverlayLoadoutProps {
    /** A carried slot waiting to be linked: the next action-bar press or click links it instead of firing. */
    linkingSlotIndex: number | null;
    onLinked: () => void;
    /** The carried slot selected in the Carried panel; action frames linked to it are outlined. */
    selectedSlotIndex: number | null;
    /** The right-click ability picker, owned by the inventory so Esc closes it before anything else. */
    abilityPicker: AbilityPickerMenu;
}

function GameOverlayLoadout({ linkingSlotIndex, onLinked, selectedSlotIndex, abilityPicker }: GameOverlayLoadoutProps): JSX.Element {
    const { bridge } = useGameplay();
    const { targets, setTargets } = useActionBarTargets();
    const essence = useGameSelector(bridge, (state) => state.player.essence);
    const maxEssence = useGameSelector(bridge, (state) => state.player.maxEssence);
    const essencePool = useGameSelector(bridge, (state) => state.player.essencePool);
    const drawPulse = useGameSelector(bridge, (state) => state.player.drawPulse);
    // Picked up as of when the pulse news arrives; the bar's animation runs it on from there.
    const essenceSweep = useMemo(() => toDrawPulseSweep(drawPulse, Date.now()), [drawPulse]);
    const [isVesselOpen, setIsVesselOpen] = useState(false);
    // The run publishes the loadout already as wielded (see RunUiPublisher), so it is read as it is.
    const equipped = useGameSelector(bridge, (state) => getStoredLoadout(state.player));
    const carriedItems = useGameSelector(bridge, (state) => state.player.carriedItems);
    const coalescences = useGameSelector(bridge, (state) => state.coalescences);
    const abilityCooldowns = useGameSelector(bridge, (state) => state.abilityCooldowns);
    const skills = useGameSelector(bridge, (state) => state.player.skills);
    const { heldAction, onActionPress, onActionRelease } = useLoadoutActionPress({ targets, setTargets, carriedItems, linkingSlotIndex, onLinked });
    const equipment = useLoadoutEquipment(equipped, carriedItems);
    const caster = useMemo(() => ({ essence, skills, wieldedLoadout: equipped }), [essence, skills, equipped]);
    const { actions, actionDetails } = useLoadoutActions(targets, carriedItems, caster, coalescences, abilityCooldowns);
    const selectedActions = useMemo(() => (selectedSlotIndex === null
        ? []
        : listCarriedSlotHotkeyIndexes(targets, selectedSlotIndex)
    ), [selectedSlotIndex, targets]);
    const drawCallout = useDrawActionCallout();
    // A new player first hears of Draw after the intro run's HUD tour, not before it.
    const isRunHudTourPending = useIsRunHudTourPending();

    return (
        <Loadout
            essence={essence}
            maxEssence={maxEssence}
            isDamageTakenShown
            essenceSweep={essenceSweep}
            canRebindKeys
            actionCallout={isRunHudTourPending ? null : drawCallout}
            onActionActivate={onActionPress}
            onActionRelease={onActionRelease}
            heldAction={heldAction}
            actions={actions}
            actionDetails={actionDetails}
            {...abilityPicker.loadoutProps}
            hasHighlightedActions={linkingSlotIndex !== null}
            selectedActions={selectedActions}
            {...equipment}
            vessel={<EssenceVessel essence={essencePool} label='Essence this run' />}
            isVesselOpen={isVesselOpen}
            onVesselOpenChange={setIsVesselOpen}
        />
    );
}

/** The crystal effects active on the local player, above the loadout. */
function GameOverlayCrystalEffects(): JSX.Element | null {
    const { bridge } = useGameplay();
    const crystalEffects = useGameSelector(bridge, (state) => state.crystalEffects);
    return <ActiveCrystalEffects effects={crystalEffects} />;
}

/** What an enemy has left on the local player, above the loadout. */
function GameOverlayAffliction(): JSX.Element | null {
    const { bridge } = useGameplay();
    const status = useGameSelector(bridge, (state) => state.player.status);
    return <ActiveAffliction status={status} />;
}

interface GameOverlayInventoryProps {
    /** Reports whether Esc has something here to dismiss, so it closes that before the run menu. */
    onDismissableChange: (hasDismissable: boolean) => void;
}

/**
 * The carried panel (left edge, toggled by the toggle-inventory binding or
 * its side label) and the loadout, sharing one item transfer selection. Esc
 * backs out one step at a time: a waiting hotkey label, then the selection,
 * then the panel.
 */
export function GameOverlayInventory({ onDismissableChange }: GameOverlayInventoryProps): JSX.Element {
    const { bridge } = useGameplay();
    const carriedItems = useGameSelector(bridge, (state) => state.player.carriedItems);
    // The picker shows each ability's cost for this character, as the loadout's details do.
    const skills = useGameSelector(bridge, (state) => state.player.skills);
    const equipped = useGameSelector(bridge, (state) => getStoredLoadout(state.player));
    const { panels, setPanelOpen } = useHudPanels();
    const isOpen = panels.inventory;
    const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(null);
    const [linkingSlotIndex, setLinkingSlotIndex] = useState<number | null>(null);
    const { selectedItemId, clearSelectedItem } = useItemTransferContext();
    const equipCarriedItem = useEquipCarriedItem();

    // The panel's selection follows the selected item: to its new slot when it
    // moves, and away when an equipped item is picked from the loadout.
    useEffect(() => {
        if (selectedItemId === null) {
            return;
        }
        const slotIndex = carriedItems.findIndex((entry) => entry?.id === selectedItemId);
        setSelectedSlotIndex(slotIndex >= 0 ? slotIndex : null);
    }, [carriedItems, selectedItemId]);

    const clearSelection = useCallback((): void => {
        clearSelectedItem();
        setSelectedSlotIndex(null);
    }, [clearSelectedItem]);
    const setOpen = useCallback((open: boolean): void => {
        setPanelOpen('inventory', open);
        if (!open) {
            setSelectedSlotIndex(null);
            setLinkingSlotIndex(null);
        }
    }, [setPanelOpen]);
    useInputBindingPress('toggle-inventory', () => setOpen(!isOpen));

    const abilityPicker = useAbilityPickerMenu(skills, equipped);
    const hasDismissable = isOpen || selectedItemId !== null || abilityPicker.isOpen;
    useEffect(() => {
        onDismissableChange(hasDismissable);
        // Unmounted (the HUD went away with the panel open): nothing here is left for Esc to dismiss.
        return () => onDismissableChange(false);
    }, [hasDismissable, onDismissableChange]);
    useInputBindingPress('open-menu', hasDismissable
        ? (): void => {
            if (abilityPicker.isOpen) {
                // The picker closes itself on Esc too; this only keeps the same press from backing out further.
                abilityPicker.close();
            } else if (linkingSlotIndex !== null) {
                setLinkingSlotIndex(null);
            } else if (selectedItemId !== null || selectedSlotIndex !== null) {
                clearSelection();
            } else {
                setOpen(false);
            }
        }
        : undefined);

    const handleEquip = useCallback((item: UIInventoryItem): void => {
        if (item.equipmentType !== undefined) {
            equipCarriedItem(item.id, item.equipmentType, isBothHanded(item));
            clearSelection();
        }
    }, [clearSelection, equipCarriedItem]);

    return (
        <>
            {/* In the top-left area; its open height keeps clear of the loadout and the party area under it. */}
            <HudAreaSlot panel="carried">
                <RunCarriedPanel
                    open={isOpen}
                    onOpenChange={setOpen}
                    selectedSlotIndex={selectedSlotIndex}
                    onSelectedSlotChange={setSelectedSlotIndex}
                    assigningSlotIndex={linkingSlotIndex}
                    onAssigningSlotChange={setLinkingSlotIndex}
                    onEquip={handleEquip}
                />
            </HudAreaSlot>
            <div className={styles.bottomCenter}>
                <GameOverlayAffliction />
                <GameOverlayCrystalEffects />
                <GameOverlayLoadout
                    linkingSlotIndex={isOpen ? linkingSlotIndex : null}
                    onLinked={() => setLinkingSlotIndex(null)}
                    selectedSlotIndex={isOpen ? selectedSlotIndex : null}
                    abilityPicker={abilityPicker}
                />
            </div>
        </>
    );
}
